/**
 * Video block — frontend behaviour.
 *
 * The video (Vimeo iframe or hosted MP4) is only loaded after the user clicks
 * the play button. Until then only the cover image is shown. A loader is
 * displayed while the player initialises.
 *
 * Once playing, Vimeo uses its native player controls and MP4 uses the native
 * <video> controls. There is no custom pause button in the middle of the video.
 */

import { trackContentEngagement } from '../../js/front/analytics';

const LOADING_CLASS = 'is-loading';
const PLAYING_CLASS = 'is-playing';

/**
 * Build the Vimeo player iframe.
 *
 * @param {string} vimeoId Numeric Vimeo ID.
 * @return {HTMLIFrameElement} Configured iframe element.
 */
const createVimeoIframe = ( vimeoId ) => {

	const iframe = document.createElement( 'iframe' );
	const params = new URLSearchParams( {
		autoplay: '1',
		title: '0',
		byline: '0',
		portrait: '0',
		dnt: '1',
	} );

	iframe.src = `https://player.vimeo.com/video/${ vimeoId }?${ params.toString() }`;
	iframe.className = 'b-video__iframe';
	iframe.setAttribute( 'allow', 'autoplay; fullscreen; picture-in-picture' );
	iframe.setAttribute( 'allowfullscreen', '' );
	iframe.setAttribute( 'title', 'Vimeo video player' );

	return iframe;

};

/**
 * Get (or create) the hosted MP4 video element inside the player container.
 *
 * When no cover photo was set the template already renders a metadata-only
 * <video> as the cover — move it into `.b-video__player` so the empty player
 * layer cannot sit on top and block native controls. Otherwise create a fresh
 * video element in the player.
 *
 * @param {HTMLElement} media    The block media container.
 * @param {string}      videoUrl Hosted MP4 file URL.
 * @return {HTMLVideoElement} The video element ready to play.
 */
const getVideoElement = ( media, videoUrl ) => {

	const player = media.querySelector( '.b-video__player' );
	let video = media.querySelector( '.b-video__media-el' );

	if ( ! video ) {

		video = document.createElement( 'video' );
		video.className = 'b-video__media-el';
		video.src = videoUrl;
		video.setAttribute( 'playsinline', '' );
		player.appendChild( video );

	} else if ( video.parentElement !== player ) {

		// First-frame poster <video>: move into the player and drop the cover
		// class / #t= fragment so native controls receive pointer events.
		video.classList.remove( 'b-video__cover' );
		video.src = videoUrl;
		player.appendChild( video );

	}

	return video;

};

/**
 * Wire up a hosted MP4 block: custom play on the cover, then native controls.
 *
 * @param {HTMLElement} media    The block media container.
 * @param {HTMLElement} button   The play button.
 * @param {string}      videoUrl Hosted MP4 file URL.
 */
const initMp4 = ( media, button, videoUrl ) => {

	if ( ! videoUrl ) {

		return;

	}

	/**
	 * Start playback once; hand off to native <video> controls afterwards.
	 */
	const play = () => {

		media.classList.add( LOADING_CLASS );

		const video = getVideoElement( media, videoUrl );

		video.controls = true;
		video.muted = false;

		// Reveal the player immediately so native controls are interactive
		// while the file buffers; drop the loader once playback starts.
		media.classList.add( PLAYING_CLASS );

		requestAnimationFrame( () => video.focus() );

		video.addEventListener( 'playing', () => {

			media.classList.remove( LOADING_CLASS );

		}, { once: true } );

		const playPromise = video.play();

		if ( playPromise && typeof playPromise.catch === 'function' ) {

			playPromise.catch( () => media.classList.remove( LOADING_CLASS ) );

		}

	};

	button.addEventListener( 'click', play, { once: true } );

};

/**
 * Wire up a Vimeo block: swap the cover for the player iframe on first click.
 * Once loaded the iframe carries Vimeo's own controls.
 *
 * @param {HTMLElement} media   The block media container.
 * @param {HTMLElement} button  The play button.
 * @param {string}      vimeoId Numeric Vimeo ID.
 */
const initVimeo = ( media, button, vimeoId ) => {

	if ( ! vimeoId ) {

		return;

	}

	/**
	 * Swap the cover for the Vimeo iframe and start loading it.
	 */
	const play = () => {

		media.classList.add( LOADING_CLASS );

		const iframe = createVimeoIframe( vimeoId );
		iframe.addEventListener( 'load', () => {

			media.classList.remove( LOADING_CLASS );
			media.classList.add( PLAYING_CLASS );

			iframe.focus();

		} );
		media.querySelector( '.b-video__player' ).appendChild( iframe );

	};

	button.addEventListener( 'click', play, { once: true } );

};

/**
 * Wire up a single video block.
 *
 * @param {HTMLElement} block The block root element.
 */
const initBlock = ( block ) => {

	const media = block.querySelector( '.b-video__media' );
	const button = block.querySelector( '.b-video__play' );

	if ( ! media || ! button ) {

		return;

	}

	const { videoType, vimeoId, videoUrl } = media.dataset;

	button.addEventListener( 'click', () => trackContentEngagement( block, 'video_play', 'play_button' ), { once: true } );

	if ( videoType === 'vimeo' ) {

		initVimeo( media, button, vimeoId );
		return;

	}

	initMp4( media, button, videoUrl );

};

document.querySelectorAll( '.b-video' ).forEach( initBlock );
